import { linter } from "@codemirror/lint"

const announcementConditionRules = {
  Vestigial: {
    type: "boolean",
    operators: ["=", "==", "!=", "!"],
    values: ["True", "False"],
    poe_version: 1
  }
}

/**
 * Path of Exile Filter Linting System
 * 
 * Provides basic error detection for PoE filter syntax.
 * Checks for common syntax errors and invalid values.
 */

// Parse condition values that may contain quoted strings
function parseQuotedValues(text, firstWord) {
  const operatorIndex = text.split(/\s+/).findIndex((word, index) => 
    index > 0 && /^(==|!=|=|!)$/.test(word)
  )
  const operator = operatorIndex > 0 ? text.split(/\s+/)[operatorIndex] : null
  
  // Parse quoted and unquoted values properly
  const afterOperator = operatorIndex > 0 ? text.substring(text.indexOf(text.split(/\s+/)[operatorIndex]) + text.split(/\s+/)[operatorIndex].length).trim() : text.substring(text.indexOf(firstWord) + firstWord.length).trim()
  
  // Extract values (quoted strings or individual words)
  const values = []
  let currentPos = 0
  while (currentPos < afterOperator.length) {
    const remaining = afterOperator.substring(currentPos).trim()
    if (!remaining) break
    
    if (remaining.startsWith('"')) {
      // Find the closing quote
      const endQuote = remaining.indexOf('"', 1)
      if (endQuote > 0) {
        values.push(remaining.substring(0, endQuote + 1))
        currentPos += endQuote + 1
      } else {
        // Unclosed quote, take the rest
        values.push(remaining)
        break
      }
    } else {
      // Find the next space or end
      const nextSpace = remaining.indexOf(' ')
      if (nextSpace > 0) {
        values.push(remaining.substring(0, nextSpace))
        currentPos += nextSpace
      } else {
        values.push(remaining)
        break
      }
    }
    
    // Skip whitespace
    while (currentPos < afterOperator.length && /\s/.test(afterOperator[currentPos])) {
      currentPos++
    }
  }
  
  return { values, operator }
}

// Helper function to safely get game data with fallback
function getGameData(key, fallback) {
  return (window.gameData && window.gameData[key] && window.gameData[key].length > 0) ? window.gameData[key] : fallback
}

function getConditionRules() {
  if (typeof window !== "undefined" && window.gameData?.conditionRules) {
    return window.gameData.conditionRules
  }

  return announcementConditionRules
}

function stripUnquotedInlineComment(text) {
  let insideQuotes = false
  let escaped = false

  for (let index = 0; index < text.length; index++) {
    const character = text[index]

    if (escaped) {
      escaped = false
    } else if (insideQuotes && character === "\\") {
      escaped = true
    } else if (character === '"') {
      insideQuotes = !insideQuotes
    } else if (!insideQuotes && character === "#") {
      return text.slice(0, index).trimEnd()
    }
  }

  return text
}

export function lintConditionLine(text, conditionRules = getConditionRules(), lineOffset = 0) {
  const trimmedText = stripUnquotedInlineComment(text).trim()
  const [condition] = trimmedText.split(/\s+/, 1)
  const rule = conditionRules[condition]

  if (!rule) {
    return []
  }

  const remainder = trimmedText.slice(condition.length).trim()
  const operatorMatch = remainder.match(/^(==|!=|>=|<=|=|!|>|<)(?:\s+|$)/)
  const operator = operatorMatch?.[1] ?? null
  const value = operator ? remainder.slice(operatorMatch[0].length).trim() : remainder
  const validOperators = Array.isArray(rule.operators) ? rule.operators : []

  if (operator && !validOperators.includes(operator)) {
    return [
      {
        from: lineOffset + text.indexOf(operator),
        to: lineOffset + text.indexOf(operator) + operator.length,
        severity: "error",
        message: `Invalid operator for ${condition}: ${operator}. Valid operators: ${validOperators.join(", ")}`
      }
    ]
  }

  if (rule.type === "boolean") {
    if (!value) {
      return [
        {
          from: lineOffset,
          to: lineOffset + text.length,
          severity: "error",
          message: `Boolean condition ${condition} requires a value (True or False)`
        }
      ]
    }

    const validValues = Array.isArray(rule.values) ? rule.values : ["True", "False"]

    if (!validValues.includes(value)) {
      const valueStart = lineOffset + Math.max(text.indexOf(value), 0)

      return [
        {
          from: valueStart,
          to: valueStart + value.length,
          severity: "error",
          message: `${condition} must be True or False, got: ${value}`
        }
      ]
    }
  }

  if (rule.type === "number") {
    if (!operator || !value) {
      return [
        {
          from: lineOffset,
          to: lineOffset + text.length,
          severity: "error",
          message: `Number condition ${condition} requires an operator and value`
        }
      ]
    }

    if (!/^-?\d+$/.test(value)) {
      return [
        {
          from: lineOffset + Math.max(text.indexOf(value), 0),
          to: lineOffset + text.length,
          severity: "error",
          message: `${condition} must be an integer, got: ${value}`
        }
      ]
    }

    const number = Number.parseInt(value, 10)

    if (
      Array.isArray(rule.range) &&
      rule.range.length === 2 &&
      (number < rule.range[0] || number > rule.range[1])
    ) {
      return [
        {
          from: lineOffset + Math.max(text.indexOf(value), 0),
          to: lineOffset + text.length,
          severity: "error",
          message: `${condition} must be between ${rule.range[0]} and ${rule.range[1]}, got: ${number}`
        }
      ]
    }
  }

  if (rule.type === "enum") {
    if (!value) {
      return [
        {
          from: lineOffset,
          to: lineOffset + text.length,
          severity: "error",
          message: `Enum condition ${condition} requires a value`
        }
      ]
    }

    const normalizedValue =
      value.length >= 2 && value.startsWith('"') && value.endsWith('"')
        ? value.slice(1, -1)
        : value
    const validValues = Array.isArray(rule.values) ? rule.values : []

    if (!validValues.includes(normalizedValue)) {
      return [
        {
          from: lineOffset + Math.max(text.indexOf(value), 0),
          to: lineOffset + text.length,
          severity: "error",
          message: `${condition} must be one of: ${validValues.join(", ")}`
        }
      ]
    }
  }

  if (rule.type === "string" && !value) {
    return [
      {
        from: lineOffset,
        to: lineOffset + text.length,
        severity: "error",
        message: `String condition ${condition} requires a value`
      }
    ]
  }

  if (rule.type === "mixed" && !value) {
    return [
      {
        from: lineOffset,
        to: lineOffset + text.length,
        severity: "error",
        message: `Mixed condition ${condition} requires a value`
      }
    ]
  }

  return []
}

export function poeFilterLinter() {
  return linter((view) => {
    const diagnostics = []
    const doc = view.state.doc
    
    // Track actions within current block for duplicate detection
    let currentBlockActions = new Map() // Map<actionName, {line: number, lineObject: object}>
    let currentBlockStartLine = null
    
    for (let i = 1; i <= doc.lines; i++) {
      const line = doc.line(i)
      const text = line.text.trim()
      
      // Skip empty lines and comments
      if (!text || text.startsWith('#')) continue
      
      const words = text.split(/\s+/)
      const firstWord = words[0]
      
      // Check for valid block types
      if (['Show', 'Hide'].includes(firstWord)) {
        // Starting a new block - reset action tracking
        currentBlockActions.clear()
        currentBlockStartLine = i
        
        // Block type should be on its own line
        if (words.length > 1) {
          diagnostics.push({
            from: line.from,
            to: line.to,
            severity: "warning",
            message: "Block types (Show/Hide) should be on their own line"
          })
        }
        continue
      }
      
      // Check for valid conditions and actions
      // Use dynamic data from API if available, fallback to hardcoded list
      const conditions = getGameData('conditions', [
        'BaseType', 'Class', 'ItemLevel', 'DropLevel', 'Quality', 'Rarity',
        'Sockets', 'LinkedSockets', 'SocketGroup', 'Height', 'Width',
        'HasExplicitMod', 'HasEnchantment', 'EnchantmentPassiveNode',
        'Corrupted', 'Mirrored', 'ElderItem', 'ShaperItem', 'FracturedItem',
        'SynthesisedItem', 'AnyEnchantment', 'HasInfluence', 'InfluenceType',
        'ElderMap', 'BlightedMap', 'UberBlightedMap', 'MapTier', 'AreaLevel',
        'GemLevel', 'StackSize', 'Replica', 'AlternateQuality', 'Identified',
        ...Object.keys(announcementConditionRules)
      ])
      
      const actions = getGameData('actions', [
        'SetTextColor', 'SetBackgroundColor', 'SetBorderColor', 'SetFontSize',
        'PlayAlertSound', 'PlayEffect', 'MinimapIcon', 'CustomAlertSound',
        'DisableDropSound', 'EnableDropSound', 'Continue', 'PlayAlertSoundPositional', 
        'CustomAlertSoundOptional', 'DisableDropSoundIfAlertSound', 'EnableDropSoundIfAlertSound'
      ])

      // Boolean conditions from Elixir module @condition_rules with type: :boolean
      const booleanConditions = getGameData('boolean_conditions', [
        'AlternateQuality', 'AnyEnchantment', 'BlightedMap', 'Corrupted', 'ElderItem', 
        'ElderMap', 'FracturedItem', 'HasImplicitMod', 'Identified', 'Mirrored', 
        'Replica', 'Scourged', 'ShapedMap', 'ShaperItem', 'SynthesisedItem', 
        'UberBlightedMap', 'ZanaMemory', 'IsVaalUnique', 'HasVaalUniqueMod'
      ])
      
      const allValidKeywords = [...conditions, ...actions]
      
      if (!allValidKeywords.includes(firstWord)) {
        diagnostics.push({
          from: line.from,
          to: line.from + firstWord.length,
          severity: "error",
          message: `Unknown keyword: ${firstWord}`
        })
        continue
      }

      if (
        conditions.includes(firstWord) &&
        Object.hasOwn(announcementConditionRules, firstWord)
      ) {
        diagnostics.push(...lintConditionLine(text, getConditionRules(), line.from))
        continue
      }
      
      // Check specific syntax rules
      if (conditions.includes(firstWord)) {                
        if (booleanConditions.includes(firstWord)) {
          // Boolean conditions need at least 2 words: condition + value
          if (words.length < 2) {
            diagnostics.push({
              from: line.from,
              to: line.to,
              severity: "error",
              message: `Boolean condition ${firstWord} requires a value (True or False)`
            })
            continue
          }
          
          // Check if operator is used (optional but not recommended)
          if (words.length >= 3 && ['=', '==', '!=', '!'].includes(words[1])) {
            // Operator is used - check if it's valid
            const operator = words[1]
            if (!['=', '==', '!=', '!'].includes(operator)) {
              const operatorStart = line.from + text.indexOf(operator)
              diagnostics.push({
                from: operatorStart,
                to: operatorStart + operator.length,
                severity: "warning",
                message: `Boolean condition ${firstWord} should not use operator "${operator}". Use: ${firstWord} True/False`
              })
            }
          }
        } else {
          // Non-boolean conditions should have operators and values
          if (words.length < 3) {
            diagnostics.push({
              from: line.from,
              to: line.to,
              severity: "error",
              message: `Condition ${firstWord} requires an operator and value`
            })
            continue
          }
        }
        
        // Check for valid operators (boolean types can optionally use operators)
        if (!booleanConditions.includes(firstWord)) {
          const operator = words[1]
          const validOperators = ['=', '==', '!=', '!', '<', '>', '<=', '>=']
          if (!validOperators.includes(operator)) {
            const operatorStart = line.from + text.indexOf(operator)
            diagnostics.push({
              from: operatorStart,
              to: operatorStart + operator.length,
              severity: "error",
              message: `Invalid operator: ${operator}. Valid operators: ${validOperators.join(', ')}`
            })
          }
        }
        
        // Note: BaseType, Class, and HasExplicitMod validation is now handled below with proper quote parsing
        
        // Check boolean values (operator optional)
        if (booleanConditions.includes(firstWord)) {
          // Determine value position based on whether operator is used
          let valueIndex = 1
          if (words.length >= 3 && ['=', '==', '!=', '!'].includes(words[1])) {
            valueIndex = 2 // Value is after operator
          }
          
          const value = words[valueIndex]
          const validBooleans = getGameData('booleans_values', ['True', 'False'])
          if (!validBooleans.includes(value)) {
            const valueStart = line.from + text.indexOf(value)
            diagnostics.push({
              from: valueStart,
              to: valueStart + value.length,
              severity: "error",
              message: `${firstWord} must be True or False, got: ${value}`
            })
          }
        }
        
        // Check rarity values - can accept multiple values like "Normal Magic"
        if (firstWord === 'Rarity') {
          const { values, operator } = parseQuotedValues(text, firstWord)
          const validRarities = getGameData('rarities', ['Normal', 'Magic', 'Rare', 'Unique'])
          
          values.forEach(value => {
            if (!value) return
            
            // Remove quotes for validation
            const cleanValue = value.replace(/^"|"$/g, '')
            
            if (!validRarities.includes(cleanValue)) {
              const valueStart = line.from + text.indexOf(value)
              diagnostics.push({
                from: valueStart,
                to: valueStart + value.length,
                severity: "error",
                message: `Invalid rarity: ${cleanValue}. Valid rarities: ${validRarities.join(', ')}`
              })
            }
          })
        }

        // Check numeric level conditions (AreaLevel, ItemLevel, DropLevel)
        if (['AreaLevel', 'ItemLevel', 'DropLevel'].includes(firstWord)) {
          const value = words[2]
          if (!/^\d+$/.test(value) || Number(value) < 0 || Number(value) > 100) {
            const valueStart = line.from + text.indexOf(value)
            diagnostics.push({
              from: valueStart,
              to: valueStart + value.length,
              severity: "error",
              message: `${firstWord} must be a number between 0-100, got: ${value}`
            })
          }
        }

        // Check Quality condition
        if (firstWord === 'Quality') {
          const value = words[2]
          if (!/^\d+$/.test(value) || Number(value) < 0 || Number(value) > 30) {
            const valueStart = line.from + text.indexOf(value)
            diagnostics.push({
              from: valueStart,
              to: valueStart + value.length,
              severity: "error",
              message: `Quality must be a number between 0-30, got: ${value}`
            })
          }
        }

        // Check LinkedSockets condition
        if (firstWord === 'LinkedSockets') {
          const value = words[2]
          if (!/^\d+$/.test(value) || Number(value) < 0 || Number(value) > 6) {
            const valueStart = line.from + text.indexOf(value)
            diagnostics.push({
              from: valueStart,
              to: valueStart + value.length,
              severity: "error",
              message: `LinkedSockets must be a number between 0-6, got: ${value}`
            })
          }
        }

        // Check SocketGroup condition
        if (firstWord === 'SocketGroup') {
          // Parse quoted and unquoted values properly
          const afterOperator = text.substring(text.indexOf(words[1]) + words[1].length).trim()
          
          // Extract values (quoted strings or individual words)
          const values = []
          let currentPos = 0
          while (currentPos < afterOperator.length) {
            const remaining = afterOperator.substring(currentPos).trim()
            if (!remaining) break
            
            if (remaining.startsWith('"')) {
              // Find the closing quote
              const endQuote = remaining.indexOf('"', 1)
              if (endQuote > 0) {
                values.push(remaining.substring(0, endQuote + 1))
                currentPos += endQuote + 1
              } else {
                // Unclosed quote, take the rest
                values.push(remaining)
                break
              }
            } else {
              // Find the next space or end
              const nextSpace = remaining.indexOf(' ')
              if (nextSpace > 0) {
                values.push(remaining.substring(0, nextSpace))
                currentPos += nextSpace
              } else {
                values.push(remaining)
                break
              }
            }
            
            // Skip whitespace
            while (currentPos < afterOperator.length && /\s/.test(afterOperator[currentPos])) {
              currentPos++
            }
          }
          
          values.forEach(value => {
            if (!value) return
            
            // Remove quotes for validation
            const cleanValue = value.replace(/^"|"$/g, '')
            
            // SocketGroup syntax: [number][RGBWDA] or just [RGBWDA]
            const socketGroupRegex = /^(\d+)?([RGBWDA]*)$/
            const match = cleanValue.match(socketGroupRegex)
            
            if (!match) {
              const valueStart = line.from + text.indexOf(value)
              diagnostics.push({
                from: valueStart,
                to: valueStart + value.length,
                severity: "error",
                message: `SocketGroup syntax invalid: ${cleanValue}. Format: [number][RGBWDA], e.g. "5GGG", "RGB", "4B"`
              })
            } else {
              const [, numPart, colorPart] = match
              
              // Validate number part if present
              if (numPart) {
                const num = parseInt(numPart, 10)
                if (num < 1 || num > 6) {
                  const valueStart = line.from + text.indexOf(value)
                  diagnostics.push({
                    from: valueStart,
                    to: valueStart + value.length,
                    severity: "error",
                    message: `SocketGroup: Link count must be between 1-6, got: ${num}`
                  })
                }
              }
              
              // Validate color part if present
              if (colorPart) {
                const colors = colorPart.split('')
                
                // Check for invalid mixing: Delve (D) cannot be linked with others
                const hasDelve = colors.includes('D')
                const hasAbyss = colors.includes('A')
                const hasRegular = colors.some(c => ['R', 'G', 'B', 'W'].includes(c))
                
                if (hasDelve && hasRegular) {
                  const valueStart = line.from + text.indexOf(value)
                  diagnostics.push({
                    from: valueStart,
                    to: valueStart + value.length,
                    severity: "warning",
                    message: `SocketGroup: Delve (D) sockets cannot be linked with regular sockets: ${cleanValue}`
                  })
                }
                
                // Abyss sockets cannot be linked (they can exist but not in linked groups)
                if (hasAbyss) {
                  const valueStart = line.from + text.indexOf(value)
                  diagnostics.push({
                    from: valueStart,
                    to: valueStart + value.length,
                    severity: "error",
                    message: `SocketGroup: Abyss (A) sockets cannot be linked, they can only exist as individual sockets: ${cleanValue}`
                  })
                }
              }
            }
          })
        }

        // Check Sockets condition
        if (firstWord === 'Sockets') {
          const value = words[2]
          if (value) {
            // Remove quotes for validation
            const cleanValue = value.replace(/^"|"$/g, '')
            
            // Sockets syntax: [number][RGBWDA] or just [RGBWDA]
            const socketsRegex = /^(\d+)?([RGBWDA]*)$/
            const match = cleanValue.match(socketsRegex)
            
            if (!match) {
              const valueStart = line.from + text.indexOf(value)
              diagnostics.push({
                from: valueStart,
                to: valueStart + value.length,
                severity: "error",
                message: `Sockets syntax invalid: ${cleanValue}. Format: [number][RGBWDA], e.g. "5GGG", "RGB", "6"`
              })
            } else {
              const [, numPart, colorPart] = match
              
              // Validate number part if present
              if (numPart) {
                const num = parseInt(numPart, 10)
                if (num < 0 || num > 6) {
                  const valueStart = line.from + text.indexOf(value)
                  diagnostics.push({
                    from: valueStart,
                    to: valueStart + value.length,
                    severity: "error",
                    message: `Sockets: Socket count must be between 0-6, got: ${num}`
                  })
                }
              }
              
              // Validate color part if present
              if (colorPart) {
                const colors = colorPart.split('')
                
                // Check for invalid mixing: Delve (D) cannot be with others
                const hasDelve = colors.includes('D')
                const hasRegular = colors.some(c => ['R', 'G', 'B', 'W'].includes(c))
                
                if (hasDelve && hasRegular) {
                  const valueStart = line.from + text.indexOf(value)
                  diagnostics.push({
                    from: valueStart,
                    to: valueStart + value.length,
                    severity: "warning",
                    message: `Sockets: Delve (D) sockets cannot be mixed with regular sockets: ${cleanValue}`
                  })
                }
              }
            }
          }
        }

        // Check Height condition
        if (firstWord === 'Height') {
          const value = words[2]
          const num = parseInt(value, 10)
          if (!/^\d+$/.test(value) || Number(value) < 1 || Number(value) > 4) {
            const valueStart = line.from + text.indexOf(value)
            diagnostics.push({
              from: valueStart,
              to: valueStart + value.length,
              severity: "error",
              message: `Height must be a pure number between 1-4, got: ${value}. Valid range: 1-4`
            })
          }
        }

        // Check Width condition
        if (firstWord === 'Width') {
          const value = words[2]
          const num = parseInt(value, 10)
          if (isNaN(num) || !/^\d+$/.test(value) || num < 1 || num > 2) {
            const valueStart = line.from + text.indexOf(value)
            diagnostics.push({
              from: valueStart,
              to: valueStart + value.length,
              severity: "error",
              message: `Width must be a pure number between 1-2, got: ${value}. Valid range: 1-2`
            })
          }
        }

        // Check HasExplicitMod condition
        if (firstWord === 'HasExplicitMod') {
          const value = words[2]
          if (!value) {
            const valueStart = line.from + text.length
            diagnostics.push({
              from: valueStart - 1,
              to: valueStart,
              severity: "error",
              message: `HasExplicitMod requires a mod name. Example: HasExplicitMod "Tyrannical"`
            })
          } else if (!value.startsWith('"') || !value.endsWith('"')) {
            const valueStart = line.from + text.indexOf(value)
            diagnostics.push({
              from: valueStart,
              to: valueStart + value.length,
              severity: "warning",
              message: `HasExplicitMod values should be quoted: "${value}". Example: HasExplicitMod "Tyrannical"`
            })
          } else if (value === '""') {
            const valueStart = line.from + text.indexOf(value)
            diagnostics.push({
              from: valueStart,
              to: valueStart + value.length,
              severity: "warning",
              message: `HasExplicitMod should not be empty. Provide a mod name like "Tyrannical"`
            })
          }
        }

        // Check HasEnchantment condition
        if (firstWord === 'HasEnchantment') {
          const value = words[2]
          if (!value) {
            const valueStart = line.from + text.length
            diagnostics.push({
              from: valueStart - 1,
              to: valueStart,
              severity: "error",
              message: `HasEnchantment requires an enchantment name. Example: HasEnchantment "Damage Penetrates"`
            })
          } else if (!value.startsWith('"') || !value.endsWith('"')) {
            const valueStart = line.from + text.indexOf(value)
            diagnostics.push({
              from: valueStart,
              to: valueStart + value.length,
              severity: "warning",
              message: `HasEnchantment values should be quoted: "${value}". Example: HasEnchantment "Damage Penetrates"`
            })
          } else if (value === '""') {
            const valueStart = line.from + text.indexOf(value)
            diagnostics.push({
              from: valueStart,
              to: valueStart + value.length,
              severity: "warning",
              message: `HasEnchantment should not be empty. Provide an enchantment name like "Damage Penetrates"`
            })
          }
        }

        // Check EnchantmentPassiveNode condition
        if (firstWord === 'EnchantmentPassiveNode') {
          const value = words[2]
          if (!value) {
            const valueStart = line.from + text.length
            diagnostics.push({
              from: valueStart - 1,
              to: valueStart,
              severity: "error",
              message: `EnchantmentPassiveNode requires an enchantment name. Example: EnchantmentPassiveNode "Minion Damage"`
            })
          } else if (!value.startsWith('"') || !value.endsWith('"')) {
            const valueStart = line.from + text.indexOf(value)
            diagnostics.push({
              from: valueStart,
              to: valueStart + value.length,
              severity: "warning",
              message: `EnchantmentPassiveNode values should be quoted: "${value}". Example: EnchantmentPassiveNode "Minion Damage"`
            })
          } else if (value === '""') {
            const valueStart = line.from + text.indexOf(value)
            diagnostics.push({
              from: valueStart,
              to: valueStart + value.length,
              severity: "warning",
              message: `EnchantmentPassiveNode should not be empty. Provide an enchantment name like "Minion Damage"`
            })
          }
        }

        // Check EnchantmentPassiveNum condition
        if (firstWord === 'EnchantmentPassiveNum') {
          const value = words[2]
          if (!/^\d+$/.test(value) || Number(value) < 0) {
            const valueStart = line.from + text.indexOf(value)
            diagnostics.push({
              from: valueStart,
              to: valueStart + value.length,
              severity: "error",
              message: `EnchantmentPassiveNum must be a non-negative number, got: ${value}`
            })
          }
        }

        // Check StackSize condition
        if (firstWord === 'StackSize') {
          const value = words[2]
          if (!/^\d+$/.test(value) || Number(value) < 1) {
            const valueStart = line.from + text.indexOf(value)
            diagnostics.push({
              from: valueStart,
              to: valueStart + value.length,
              severity: "error",
              message: `StackSize must be a number 1 or higher, got: ${value}`
            })
          }
        }

        // Check GemLevel condition
        if (firstWord === 'GemLevel') {
          const value = words[2]
          if (!/^\d+$/.test(value) || Number(value) < 1 || Number(value) > 21) {
            const valueStart = line.from + text.indexOf(value)
            diagnostics.push({
              from: valueStart,
              to: valueStart + value.length,
              severity: "error",
              message: `GemLevel must be a number between 1-21, got: ${value}`
            })
          }
        }

        // Check CorruptedMods condition
        if (firstWord === 'CorruptedMods') {
          const value = words[2]
          if (!/^\d+$/.test(value) || Number(value) < 0) {
            const valueStart = line.from + text.indexOf(value)
            diagnostics.push({
              from: valueStart,
              to: valueStart + value.length,
              severity: "error",
              message: `CorruptedMods must be a non-negative number, got: ${value}`
            })
          }
        }

        // Check HasInfluence condition
        if (firstWord === 'HasInfluence') {
          const value = words[2]
          const validInfluences = getGameData('influences', ['Shaper', 'Elder', 'Crusader', 'Hunter', 'Redeemer', 'Warlord', 'None'])
          if (!validInfluences.includes(value)) {
            const valueStart = line.from + text.indexOf(value)
            diagnostics.push({
              from: valueStart,
              to: valueStart + value.length,
              severity: "error",
              message: `HasInfluence must be one of: ${validInfluences.join(', ')}. Got: ${value}`
            })
          }
        }

        // Check HasSearingExarchImplicit condition
        if (firstWord === 'HasSearingExarchImplicit') {
          const value = words[2]
          if (!/^\d+$/.test(value) || Number(value) < 0 || Number(value) > 6) {
            const valueStart = line.from + text.indexOf(value)
            diagnostics.push({
              from: valueStart,
              to: valueStart + value.length,
              severity: "error",
              message: `HasSearingExarchImplicit must be a number between 0-6, got: ${value}`
            })
          }
        }

        // Check HasEaterOfWorldsImplicit condition
        if (firstWord === 'HasEaterOfWorldsImplicit') {
          const value = words[2]
          if (!/^\d+$/.test(value) || Number(value) < 0 || Number(value) > 6) {
            const valueStart = line.from + text.indexOf(value)
            diagnostics.push({
              from: valueStart,
              to: valueStart + value.length,
              severity: "error",
              message: `HasEaterOfWorldsImplicit must be a number between 0-6, got: ${value}`
            })
          }
        }

        // Check MapTier condition
        if (firstWord === 'MapTier') {
          const value = words[2]
          if (!/^\d+$/.test(value) || Number(value) < 1 || Number(value) > 17) {
            const valueStart = line.from + text.indexOf(value)
            diagnostics.push({
              from: valueStart,
              to: valueStart + value.length,
              severity: "error",
              message: `MapTier must be a number between 1-17, got: ${value}`
            })
          }
        }







        // Check Class values (partial matches allowed from game data)
        if (firstWord === 'Class') {        
          const { values, operator } = parseQuotedValues(text, firstWord)
          
          values.forEach(value => {
            // Skip quoted values (assume they're intentional)
            if (!value || (value.startsWith('"') && value.endsWith('"'))) return
            
            const gameData = window.gameData || {}
            if (gameData.itemClasses && gameData.itemClasses.length > 0 && value.length > 2) {
              let hasMatch = false
              
              if (operator === '==') {
                // Exact match required for ==
                hasMatch = gameData.itemClasses.some(cls => 
                  cls.toLowerCase() === value.toLowerCase()
                )
              } else {
                // Partial match allowed for = and !=
                hasMatch = gameData.itemClasses.some(cls => 
                  cls.toLowerCase().includes(value.toLowerCase()) || 
                  value.toLowerCase().includes(cls.toLowerCase())
                )
              }
              
              if (!hasMatch) {
                const valueStart = line.from + text.indexOf(value)
                const matchType = operator === '==' ? 'exact' : 'partial'
                diagnostics.push({
                  from: valueStart,
                  to: valueStart + value.length,
                  severity: "warning",
                  message: `Class "${value}" doesn't match any known item classes (${matchType} match required). Examples: "Currency Item", "Helmet", "Ring"`
                })
              }
            }
          })
        }

        // Check BaseType values (partial matches allowed from game data)
        if (firstWord === 'BaseType') {          
          const { values, operator } = parseQuotedValues(text, firstWord)
          
          values.forEach(value => {
            // Skip quoted values (assume they're intentional)
            if (!value || (value.startsWith('"') && value.endsWith('"'))) return
            
            const gameData = window.gameData || {}
            if (gameData.baseTypes && gameData.baseTypes.length > 0 && value.length > 2) {
              let hasMatch = false
              
              if (operator === '==') {
                // Exact match required for ==
                hasMatch = gameData.baseTypes.some(baseType => 
                  baseType.toLowerCase() === value.toLowerCase()
                )
              } else {
                // Partial match allowed for = and != (or no operator)
                hasMatch = gameData.baseTypes.some(baseType => 
                  baseType.toLowerCase().includes(value.toLowerCase()) || 
                  value.toLowerCase().includes(baseType.toLowerCase())
                )
              }
              
              if (!hasMatch) {
                const valueStart = line.from + text.indexOf(value)
                const matchType = operator === '==' ? 'exact' : 'partial'
                diagnostics.push({
                  from: valueStart,
                  to: valueStart + value.length,
                  severity: "warning",
                  message: `BaseType "${value}" doesn't match any known item names (${matchType} match required). Examples: "Chaos Orb", "Royal Burgonet", "Diamond Ring"`
                })
              }
            }
          })
        }
      }
      
      if (actions.includes(firstWord)) {
        // Check for duplicate actions within the same block
        if (currentBlockActions.has(firstWord)) {
          const previousAction = currentBlockActions.get(firstWord)
          diagnostics.push({
            from: line.from,
            to: line.from + firstWord.length,
            severity: "error",
            message: `Duplicate action "${firstWord}" found. Previous occurrence at line ${previousAction.line}. Each action should only appear once per block.`
          })
        } else {
          // Track this action for duplicate detection
          currentBlockActions.set(firstWord, { line: i, lineObject: line })
        }
        
        // Define actions that don't require parameters
        const noParamActions = ['DisableDropSound', 'EnableDropSound', 'Continue']
        
        // Check if action requires parameters (most do, but some don't)
        if (!noParamActions.includes(firstWord) && words.length < 2) {
          diagnostics.push({
            from: line.from,
            to: line.to,
            severity: "error",
            message: `Action ${firstWord} requires a value`
          })
          continue
        }
        
        // Check color actions (should have 3 or 4 RGB values)
        if (['SetTextColor', 'SetBackgroundColor', 'SetBorderColor'].includes(firstWord)) {
          const colorValues = words.slice(1)
          if (colorValues.length < 3 || colorValues.length > 4) {
            diagnostics.push({
              from: line.from,
              to: line.to,
              severity: "error",
              message: `${firstWord} requires 3 or 4 color values (RGB or RGBA)`
            })
          } else {
            // Check if values are valid numbers (0-255)
            colorValues.forEach((value, index) => {
              if (!/^\d+$/.test(value) || Number(value) < 0 || Number(value) > 255) {
                const valueStart = line.from + text.indexOf(value)
                diagnostics.push({
                  from: valueStart,
                  to: valueStart + value.length,
                  severity: "error",
                  message: `Color value must be a number between 0-255, got: ${value}`
                })
              }
            })
          }
        }
        
        // Check font size
        if (firstWord === 'SetFontSize') {
          const fontSize = words[1]
          const validFontSizes = getGameData('font_sizes', Array.from({length: 100}, (_, i) => i + 1))
          if (!/^\d+$/.test(fontSize) || !validFontSizes.includes(Number(fontSize))) {
            const valueStart = line.from + text.indexOf(fontSize)
            diagnostics.push({
              from: valueStart,
              to: valueStart + fontSize.length,
              severity: "warning",
              message: `Font size should be a number between 1-100, got: ${fontSize}`
            })
          }
        }
        


        // Check PlayEffect action
        if (firstWord === 'PlayEffect') {
          const effectValues = words.slice(1) // Skip the action name
          
          if (effectValues.length === 0) {
            const valueStart = line.from + text.length
            diagnostics.push({
              from: valueStart - 1,
              to: valueStart,
              severity: "error",
              message: `PlayEffect requires a color parameter or "None" to disable. Format: PlayEffect <Color> [Temp] or PlayEffect None`
            })
          } else if (effectValues.length > 2) {
            const valueStart = line.from + text.indexOf(effectValues[2])
            diagnostics.push({
              from: valueStart,
              to: line.to,
              severity: "error",
              message: `PlayEffect takes maximum 2 parameters. Format: PlayEffect <Color> [Temp] or PlayEffect None`
            })
          } else {
            // Validate color parameter
            const color = effectValues[0]
            if (color === 'None') {
              // Valid disable syntax - no temp parameter allowed
              if (effectValues.length > 1) {
                const valueStart = line.from + text.indexOf(effectValues[1])
                diagnostics.push({
                  from: valueStart,
                  to: line.to,
                  severity: "error",
                  message: `PlayEffect None cannot have additional parameters. Use: PlayEffect None`
                })
              }
            } else {
              // Validate color
              const validColors = getGameData('effect_colors', ['Red', 'Green', 'Blue', 'Brown', 'White', 'Yellow', 'Cyan', 'Grey', 'Orange', 'Pink', 'Purple'])
              if (!validColors.includes(color)) {
                const valueStart = line.from + text.indexOf(color)
                diagnostics.push({
                  from: valueStart,
                  to: valueStart + color.length,
                  severity: "error",
                  message: `PlayEffect color must be one of: ${validColors.join(', ')}, or "None" to disable, got: ${color}`
                })
              }
              
              // Validate optional Temp parameter
              if (effectValues.length === 2) {
                const tempParam = effectValues[1]
                if (tempParam !== 'Temp') {
                  const valueStart = line.from + text.indexOf(tempParam)
                  diagnostics.push({
                    from: valueStart,
                    to: valueStart + tempParam.length,
                    severity: "error",
                    message: `PlayEffect second parameter must be "Temp" for temporary beam, got: ${tempParam}`
                  })
                }
              }
            }
          }
        }

        // Check PlayAlertSound and PlayAlertSoundPositional actions
        if (['PlayAlertSound', 'PlayAlertSoundPositional'].includes(firstWord)) {
          const soundValues = words.slice(1) // Skip the action name
          
          if (soundValues.length === 0) {
            const valueStart = line.from + text.length
            diagnostics.push({
              from: valueStart - 1,
              to: valueStart,
              severity: "error",
              message: `${firstWord} requires a sound ID. Format: ${firstWord} <Id> [Volume] (Id: 1-16 or None, Volume: 0-300)`
            })
          } else if (soundValues.length > 2) {
            const valueStart = line.from + text.indexOf(soundValues[2])
            diagnostics.push({
              from: valueStart,
              to: line.to,
              severity: "error",
              message: `${firstWord} takes maximum 2 parameters. Format: ${firstWord} <Id> [Volume] (Id: 1-16 or None, Volume: 0-300)`
            })
          } else {
            // Validate sound ID (first parameter)
            const soundId = soundValues[0]
            if (soundId !== 'None') {
              const validSoundIds = getGameData('sound_ids', Array.from({length: 16}, (_, i) => i + 1))
              if (!/^\d+$/.test(soundId) || !validSoundIds.includes(Number(soundId))) {
                const valueStart = line.from + text.indexOf(soundId)
                diagnostics.push({
                  from: valueStart,
                  to: valueStart + soundId.length,
                  severity: "error",
                  message: `${firstWord} ID must be a number 1-16 or "None", got: ${soundId}`
                })
              }
            }
            
            // Validate volume (second parameter, optional)
            if (soundValues.length === 2) {
              const volume = soundValues[1]
              if (!/^\d+$/.test(volume) || Number(volume) < 0 || Number(volume) > 300) {
                const valueStart = line.from + text.indexOf(volume)
                diagnostics.push({
                  from: valueStart,
                  to: valueStart + volume.length,
                  severity: "error",
                  message: `${firstWord} volume must be a number between 0-300, got: ${volume}`
                })
              }
            }
          }
        }

        // Check DisableDropSound and EnableDropSound actions (no parameters)
        if (['DisableDropSound', 'EnableDropSound', 'Continue'].includes(firstWord)) {
          if (words.length > 1) {
            const valueStart = line.from + text.indexOf(words[1])
            diagnostics.push({
              from: valueStart,
              to: line.to,
              severity: "error",
              message: `${firstWord} takes no parameters. Format: ${firstWord}`
            })
          }
        }

        // Check CustomAlertSound action
        if (firstWord === 'CustomAlertSound') {
          if (words.length === 1) {
            const valueStart = line.from + text.length
            diagnostics.push({
              from: valueStart - 1,
              to: valueStart,
              severity: "error",
              message: `CustomAlertSound requires a file path. Format: CustomAlertSound <FileName | FileFullPath> (use quotes for paths with spaces, "None" to disable)`
            })
          } else {
            // Check if we have a quoted string or unquoted path
            const restOfLine = text.substring(text.indexOf(words[1]))
            
            // If it starts with a quote, validate quoted string
            if (restOfLine.startsWith('"')) {
              const quoteMatch = restOfLine.match(/^"([^"]*)"/)
              if (!quoteMatch) {
                const valueStart = line.from + text.indexOf(words[1])
                diagnostics.push({
                  from: valueStart,
                  to: line.to,
                  severity: "error",
                  message: `CustomAlertSound: Unclosed quote in file path. Use quotes for paths with spaces: "path/to/file.mp3"`
                })
              } else {
                const filePath = quoteMatch[1]
                if (filePath === "") {
                  const valueStart = line.from + text.indexOf(words[1])
                  diagnostics.push({
                    from: valueStart,
                    to: valueStart + quoteMatch[0].length,
                    severity: "error",
                    message: `CustomAlertSound: Empty file path. Use "None" to disable or provide a valid file path`
                  })
                }
                // Check for extra parameters after quoted string
                const afterQuote = restOfLine.substring(quoteMatch[0].length).trim()
                if (afterQuote.length > 0) {
                  const extraStart = line.from + text.indexOf(words[1]) + quoteMatch[0].length
                  diagnostics.push({
                    from: extraStart,
                    to: line.to,
                    severity: "error",
                    message: `CustomAlertSound takes only one parameter. Extra text found: ${afterQuote}`
                  })
                }
              }
            } else {
              // Unquoted path - check for multiple words (should be quoted)
              if (words.length > 2) {
                const valueStart = line.from + text.indexOf(words[2])
                diagnostics.push({
                  from: valueStart,
                  to: line.to,
                  severity: "warning",
                  message: `CustomAlertSound: File path with spaces should be quoted. Use: CustomAlertSound "path with spaces.mp3"`
                })
              }
              // Validate single unquoted path
              const filePath = words[1]
              if (filePath === 'None') {
                // "None" should be quoted for consistency
                const valueStart = line.from + text.indexOf(words[1])
                diagnostics.push({
                  from: valueStart,
                  to: valueStart + filePath.length,
                  severity: "warning",
                  message: `CustomAlertSound: Use quoted "None" to disable. Format: CustomAlertSound "None"`
                })
              }
            }
          }
        }

        // Check MinimapIcon action
        if (firstWord === 'MinimapIcon') {
          const iconValues = words.slice(1) // Skip the action name
          
          if (iconValues.length === 0) {
            const valueStart = line.from + text.length
            diagnostics.push({
              from: valueStart - 1,
              to: valueStart,
              severity: "error",
              message: `MinimapIcon requires 3 parameters or -1 to disable. Format: MinimapIcon <Size> <Color> <Shape> or MinimapIcon -1`
            })
          } else if (iconValues.length === 1 && iconValues[0] === '-1') {
            // Valid disable syntax
          } else if (iconValues.length !== 3) {
            const valueStart = iconValues.length === 0 ? line.from + text.length : line.from + text.indexOf(iconValues[iconValues.length - 1]) + iconValues[iconValues.length - 1].length
            diagnostics.push({
              from: valueStart,
              to: line.to,
              severity: "error",
              message: `MinimapIcon requires exactly 3 parameters (Size, Color, Shape) or -1 to disable. Got ${iconValues.length} parameter(s)`
            })
          } else {
            // Validate each parameter
            const [size, color, shape] = iconValues
            
            // Validate size (0, 1, 2)
            const validSizes = getGameData('minimap_icon_sizes', [0, 1, 2])
            if (!/^\d+$/.test(size) || !validSizes.includes(Number(size))) {
              const valueStart = line.from + text.indexOf(size)
              diagnostics.push({
                from: valueStart,
                to: valueStart + size.length,
                severity: "error",
                message: `MinimapIcon size must be a number: 0 (largest), 1 (medium), or 2 (small), got: ${size}`
              })
            }
            
            // Validate color
            const validColors = getGameData('effect_colors', ['Red', 'Green', 'Blue', 'Brown', 'White', 'Yellow', 'Cyan', 'Grey', 'Orange', 'Pink', 'Purple'])
            if (!validColors.includes(color)) {
              const valueStart = line.from + text.indexOf(color)
              diagnostics.push({
                from: valueStart,
                to: valueStart + color.length,
                severity: "error",
                message: `MinimapIcon color must be one of: ${validColors.join(', ')}, got: ${color}`
              })
            }
            
            // Validate shape
            const validShapes = getGameData('minimap_icon_shapes', ['Circle', 'Diamond', 'Hexagon', 'Square', 'Star', 'Triangle', 'Cross', 'Moon', 'Raindrop', 'Kite', 'Pentagon', 'UpsideDownHouse'])
            if (!validShapes.includes(shape)) {
              const valueStart = line.from + text.indexOf(shape)
              diagnostics.push({
                from: valueStart,
                to: valueStart + shape.length,
                severity: "error",
                message: `MinimapIcon shape must be one of: ${validShapes.join(', ')}, got: ${shape}`
              })
            }
          }
        }
      }
    }
    
    return diagnostics
  })
}
